import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';

/**
 * Loss Quantity Cell Renderer Component
 *
 * Cell renderer for "Cantidad Merma" (loss/shrinkage quantity) in loss reports.
 * Shows loss quantity with base and parallel units.
 * Reads from detail fields.
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'detail.soldQuantity',
 *   headerName: 'Cantidad Merma',
 *   cellRenderer: LossQuantityCellRendererComponent,
 *   cellRendererParams: {
 *     getUMPActive: () => this.UMPActive
 *   }
 * }
 * ```
 */
@Component({
  selector: "app-loss-quantity-cell-renderer",
  standalone: true,
  imports: [CommonModule, DecimalPipe, UnitMeasureDisplayComponent],
  template: `
    <app-unit-measure-display
      [usedUnit]="{
        quantity: params?.data?.detail?.soldQuantity,
        unitId: params?.data?.detail?.soldUnitOfMeasure?._id,
        unitName: params?.data?.detail?.soldUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.detail?.soldUnitOfMeasure?.code,
      }"
      [baseUnit]="{
        quantity: params?.data?.detail?.baseQuantity,
        unitId: params?.data?.detail?.baseUnitOfMeasure?._id,
        unitName: params?.data?.detail?.baseUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.detail?.baseUnitOfMeasure?.code,
      }"
      [parallelUnit]="{
        quantity: params?.data?.detail?.pararellQuantity,
        unitId: params?.data?.detail?.pararellUnitOfMeasure?._id,
        unitName: params?.data?.detail?.pararellUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.detail?.pararellUnitOfMeasure?.code,
      }"
      [material]="{
        _id: params?.data?.detail?.material?._id,
        code: params?.data?.detail?.material?.code,
        UMPActive: params?.data?.detail?.material?.UMPActive,
      }"
      [companyUMPActive]="isUMPActive"
      [statusId]="params.statusId"
    ></app-unit-measure-display>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LossQuantityCellRendererComponent
  implements CellRenderer<any, any>
{
  @Input() params!: CellRendererParams<any, any>;

  private readonly decimalPipe = new DecimalPipe("en-US");

  get isUMPActive(): boolean {
    if (this.params?.getUMPActive) {
      return this.params.getUMPActive();
    }
    return false;
  }

  get soldQuantity(): string {
    const value = this.params?.data?.detail?.soldQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  get soldUnitName(): string {
    return this.params?.data?.detail?.soldUnitOfMeasure?.name ?? "-";
  }

  get baseQuantity(): string {
    const value = this.params?.data?.detail?.baseQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  get baseUnitName(): string {
    return this.params?.data?.detail?.baseUnitOfMeasure?.name ?? "-";
  }

  get hasParallelUnit(): boolean {
    return !!this.params?.data?.detail?.pararellUnitOfMeasure;
  }

  get parallelQuantity(): string {
    const value = this.params?.data?.detail?.pararellQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  get parallelUnitName(): string {
    return this.params?.data?.detail?.pararellUnitOfMeasure?.name ?? "-";
  }
}
